import type { ChildControls, ParentPinRecord, ParentSettingsV1 } from "./parentTypes";

export const MIN_PIN_LENGTH = 4;
export const MAX_PIN_LENGTH = 6;
export const PIN_SALT_BYTES = 16;
export const PIN_HASH_BYTES = 32;
export const PIN_PBKDF2_ITERATIONS = 150_000;
export const MAX_PIN_PBKDF2_ITERATIONS = 1_000_000;

const PIN_PATTERN = /^\d{4,6}$/;
const BASE64_PATTERN = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

export function isValidPin(pin: string): boolean {
  return PIN_PATTERN.test(pin);
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

export function base64ToBytes(value: string, maximumBytes = 1024): Uint8Array {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > Math.ceil(maximumBytes / 3) * 4 + 4 ||
    value.length % 4 !== 0 ||
    !BASE64_PATTERN.test(value)
  ) {
    throw new Error("Invalid encoded value.");
  }
  let binary: string;
  try {
    binary = atob(value);
  } catch {
    throw new Error("Invalid encoded value.");
  }
  if (binary.length > maximumBytes) throw new Error("Encoded value is too large.");
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  if (bytesToBase64(bytes) !== value) throw new Error("Invalid encoded value.");
  return bytes;
}

export function constantTimeBytesEqual(left: Uint8Array, right: Uint8Array): boolean {
  const length = Math.max(left.length, right.length);
  let difference = left.length ^ right.length;
  for (let index = 0; index < length; index += 1) {
    difference |= (left[index] ?? 0) ^ (right[index] ?? 0);
  }
  return difference === 0;
}

async function derivePinBytes(
  pin: string,
  salt: Uint8Array,
  iterations: number,
): Promise<Uint8Array> {
  if (!isValidPin(pin)) throw new Error("PIN must contain 4 to 6 digits.");
  if (salt.byteLength !== PIN_SALT_BYTES) throw new Error("PIN salt is invalid.");
  if (
    !Number.isInteger(iterations) ||
    iterations < PIN_PBKDF2_ITERATIONS ||
    iterations > MAX_PIN_PBKDF2_ITERATIONS
  ) {
    throw new Error("PIN derivation settings are invalid.");
  }
  const material = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(pin),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-256", salt: new Uint8Array(salt).buffer, iterations },
    material,
    PIN_HASH_BYTES * 8,
  );
  return new Uint8Array(bits);
}

export async function createPinRecord(pin: string): Promise<ParentPinRecord> {
  if (!isValidPin(pin)) throw new Error("PIN must contain 4 to 6 digits.");
  const salt = crypto.getRandomValues(new Uint8Array(PIN_SALT_BYTES));
  const hash = await derivePinBytes(pin, salt, PIN_PBKDF2_ITERATIONS);
  return {
    saltBase64: bytesToBase64(salt),
    hashBase64: bytesToBase64(hash),
    iterations: PIN_PBKDF2_ITERATIONS,
  };
}

export async function verifyPin(pin: string, record: ParentPinRecord): Promise<boolean> {
  if (!isValidPin(pin)) return false;
  let salt: Uint8Array;
  let expected: Uint8Array;
  try {
    salt = base64ToBytes(record.saltBase64, PIN_SALT_BYTES);
    expected = base64ToBytes(record.hashBase64, PIN_HASH_BYTES);
  } catch {
    return false;
  }
  if (salt.byteLength !== PIN_SALT_BYTES || expected.byteLength !== PIN_HASH_BYTES) return false;
  const actual = await derivePinBytes(pin, salt, record.iterations);
  return constantTimeBytesEqual(actual, expected);
}

export async function changeSettingsPin(
  settings: ParentSettingsV1,
  newPin: string,
): Promise<ParentSettingsV1> {
  return {
    version: 1,
    pin: await createPinRecord(newPin),
    controls: { ...settings.controls },
  };
}

export function settingsWithPin(pin: ParentPinRecord, controls: ChildControls): ParentSettingsV1 {
  return { version: 1, pin: { ...pin }, controls: { ...controls } };
}
